import axios from 'axios';
import config from '../config';
import { encryptJSON, decryptResponse, isEncryptedResponse } from './encryptionUtils';

const ENCRYPTED_LOGIN_ENDPOINTS = [
  '/suite/login/',
];

const shouldEncryptAllPayloads = () => {
  return process.env.REACT_APP_ENCRYPT_ALL_PAYLOAD?.toLowerCase() === 'true';
};

const shouldDecryptAllResponses = () => {
  return process.env.REACT_APP_DECRYPT_ALL_RESPONSE?.toLowerCase() === 'true';
};

// REACT_APP_ENCRYPT_ALL_PAYLOAD=true → all APIs (base URL); false → login only. Caller must never pass FormData.
const requiresEncryption = (url, baseURL) => {
  const isToBaseURL = baseURL && baseURL === config.API_BASE_URL;
  if (!isToBaseURL) return false;
  if (shouldEncryptAllPayloads()) return true;
  return ENCRYPTED_LOGIN_ENDPOINTS.some(pattern => url.includes(pattern));
};

const requiresDecryption = (url, baseURL) => {
  // Only decrypt responses from our API (relative path or full URL)
  const isFromBaseURL = (baseURL && baseURL === config.API_BASE_URL) ||
    (config.API_BASE_URL && url && typeof url === 'string' && url.startsWith(config.API_BASE_URL));
  if (!isFromBaseURL) return false;
  // REACT_APP_DECRYPT_ALL_RESPONSE=true → decrypt all; false → decrypt only login
  if (shouldDecryptAllResponses()) return true;
  return ENCRYPTED_LOGIN_ENDPOINTS.some(pattern => url.includes(pattern));
};

const apiClient = axios.create({
  baseURL: config.API_BASE_URL,
});

apiClient.interceptors.request.use(
  (config) => {
    if (!config.url?.includes('/login')) {
      const accessToken = sessionStorage.getItem('access_token');
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
    }

    const url = config.url || '';
    const baseURL = config.baseURL;
    
    const method = config.method?.toLowerCase();
    const isFormData = config.data instanceof FormData;
    // REACT_APP_ENCRYPT_ALL_PAYLOAD: true = all APIs, false = login only. FormData is never encrypted.
    if (
      !isFormData &&
      requiresEncryption(url, baseURL) &&
      config.data &&
      ['post', 'put', 'patch'].includes(method)
    ) {
      try {
        const encryptedData = encryptJSON(config.data);
        config.data = { data: encryptedData };
      } catch (error) {
        throw new Error(`Request encryption failed: ${error.message}`);
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => {
    if (response.status === 200) {
      try {
        sessionStorage.setItem("LMS_lastActivityTime", Date.now().toString());
      } catch (error) {
      }
    }

    const url = response.config.url || '';
    const baseURL = response.config.baseURL;
    // REACT_APP_DECRYPT_ALL_RESPONSE: true = all APIs, false = login only (same format as Admin: decryptResponse)
    if (requiresDecryption(url, baseURL) && response.data && isEncryptedResponse(response)) {
      try {
        response.data = decryptResponse(response);
      } catch (error) {
      }
    }

    return response;
  },
  (error) => {
    // Only forced-logout on auth failure. 403 (forbidden) can be role-gated
    // secondary calls (e.g. admin-only) and must not wipe a successful CC save.
    if (error.response?.status === 401) {
      sessionStorage.clear();
      localStorage.clear();
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

// Helper functions for common HTTP methods
export const apiGet = async (url, config = {}) => {
  return apiClient.get(url, config);
};

export const apiPost = async (url, data = {}, config = {}) => {
  return apiClient.post(url, data, config);
};

export const apiPut = async (url, data = {}, config = {}) => {
  return apiClient.put(url, data, config);
};

export const apiDelete = async (url, config = {}) => {
  return apiClient.delete(url, config);
};

export const apiPostFormData = async (url, formData, config = {}) => {
  return apiClient.post(url, formData, {
    ...config,
    headers: {
      ...config.headers,
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const apiPutFormData = async (url, formData, config = {}) => {
  return apiClient.put(url, formData, {
    ...config,
    headers: {
      ...config.headers,
      'Content-Type': 'multipart/form-data',
    },
  });
};

// Helper functions with custom baseURL support
const createCustomApiCall = (method) => async (url, dataOrConfig = {}, config = {}, customBaseURL = null) => {
  if (customBaseURL) {
    // Create a temporary client with custom baseURL but same interceptors
    const customClient = axios.create({
      baseURL: customBaseURL,
    });
    
    // Apply custom request interceptor that handles URL-based encryption
    customClient.interceptors.request.use(
      (config) => {
        if (!config.url?.includes('/login')) {
          const accessToken = sessionStorage.getItem('access_token');
          if (accessToken) {
            config.headers.Authorization = `Bearer ${accessToken}`;
          }
        }

        const url = config.url || '';
        const baseURL = config.baseURL;
        
        const method = config.method?.toLowerCase();
        const isFormData = config.data instanceof FormData;
        // REACT_APP_ENCRYPT_ALL_PAYLOAD: true = all APIs, false = login only. FormData is never encrypted.
        if (
          !isFormData &&
          requiresEncryption(url, baseURL) &&
          config.data &&
          ['post', 'put', 'patch'].includes(method)
        ) {
          try {
            const encryptedData = encryptJSON(config.data);
            config.data = { data: encryptedData };
          } catch (error) {
            throw new Error(`Request encryption failed: ${error.message}`);
          }
        }

        return config;
      },
      (error) => Promise.reject(error)
    );
    
    // Apply custom response interceptor (same env-driven decrypt logic)
    customClient.interceptors.response.use(
      (response) => {
        if (response.status === 200) {
          try {
            sessionStorage.setItem("LMS_lastActivityTime", Date.now().toString());
          } catch (error) {
          }
        }

        const url = response.config.url || '';
        const baseURL = response.config.baseURL;
        if (requiresDecryption(url, baseURL) && response.data && isEncryptedResponse(response)) {
          try {
            response.data = decryptResponse(response);
          } catch (error) {
          }
        }

        return response;
      },
      apiClient.interceptors.response.handlers[0].rejected
    );
    
    // Call the appropriate method
    if (method === 'get' || method === 'delete') {
      return customClient[method](url, dataOrConfig);
    } else {
      return customClient[method](url, dataOrConfig, config);
    }
  }
  
  // Fallback to regular apiClient
  if (method === 'get' || method === 'delete') {
    return apiClient[method](url, dataOrConfig);
  } else {
    return apiClient[method](url, dataOrConfig, config);
  }
};

export const apiGetWithBaseURL = createCustomApiCall('get');
export const apiPostWithBaseURL = createCustomApiCall('post');
export const apiPutWithBaseURL = createCustomApiCall('put');
export const apiDeleteWithBaseURL = createCustomApiCall('delete');

// Helper to get the API client
export const getApiClient = () => apiClient;

// Standardized logout function
export const performLogout = async (studentId, isInactivityLogout = false, forceLogout = false) => {
  let url = `${process.env.REACT_APP_API_BASE_URL}api/logout/${studentId}`;
  if (isInactivityLogout) url += '/SESSION_TIMEOUT';
  else if (forceLogout) url += '/FORCE_LOGOUT';
  else url += '/';

  const accessToken = sessionStorage.getItem("access_token");
  if (accessToken) {
    try {
      await apiClient.get(url, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
    } catch (error) {
    }
  }
  sessionStorage.clear();
  localStorage.clear();
  window.location.href = '/';
};

export default apiClient; 
